import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import './ContactModal.css';

export const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Try to send via backend API first
      const response = await fetch('https://liceron-backend-1044318315452.us-central1.run.app/api/sales-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // Success - show confirmation
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          onClose();
          setFormData({ name: '', email: '', company: '', message: '' });
        }, 3000);
      } else {
        throw new Error('Failed to send message via API');
      }
    } catch (err) {
      // Fallback to mailto if API fails
      console.log('API failed, using mailto fallback:', err);
      
      const subject = `Sales Inquiry from ${formData.name}${formData.company ? ` - ${formData.company}` : ''}`;
      const body = `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company || 'Not provided'}\n\nMessage:\n${formData.message}`;
      
      const mailtoLink = `mailto:sales@liceron.in?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
      
      // Show success after a delay
      setTimeout(() => {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          onClose();
          setFormData({ name: '', email: '', company: '', message: '' });
        }, 2000);
      }, 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="contact-modal">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="modal-title">
                Start a Conversation
              </DialogTitle>
              <DialogDescription className="modal-description">
                Share your use case—we’ll respond with next steps and a recommended approach.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <Label htmlFor="name" className="form-label">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email" className="form-label">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your.email@company.com"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="company" className="form-label">Company</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your company name"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="message" className="form-label">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="form-textarea"
                  placeholder="Tell us about your AI needs..."
                  rows={4}
                />
              </div>

              <Button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
              
              {error && (
                <p className="error-message" style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
                  {error}
                </p>
              )}
            </form>
          </>
        ) : (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3 className="success-title">Message Sent!</h3>
            <p className="success-text">We'll get back to you within 24 hours.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};